/**
 * PhotoGrid Component
 * 
 * Displays photos in a responsive grid with infinite scroll.
 */

import React, { useCallback, useState } from "react";
import {
  View,
  Text,
  FlatList,
  RefreshControl,
  ActivityIndicator,
} from "react-native";
import { useThemeColors } from "@/providers/ThemeProvider";
import { PhotoCard } from "./PhotoCard";
import { PhotoSelectionBar } from "./PhotoSelectionBar";
import type { PhotoWithRelations } from "../types";

interface PhotoGridProps {
  photos: PhotoWithRelations[];
  isLoading: boolean;
  isRefreshing: boolean;
  hasMore: boolean;
  onRefresh: () => void;
  onLoadMore: () => void;
  onPhotoPress: (photo: PhotoWithRelations) => void;
  onDownloadSelected?: (photoIds: string[]) => void;
  emptyMessage?: string;
  showStats?: boolean;
}

/**
 * Photo grid with selection support
 */
export const PhotoGrid: React.FC<PhotoGridProps> = ({
  photos,
  isLoading,
  isRefreshing,
  hasMore,
  onRefresh,
  onLoadMore,
  onPhotoPress,
  onDownloadSelected,
  emptyMessage = "Geen foto's gevonden",
  showStats = true,
}) => {
  const colors = useThemeColors();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isSelecting, setIsSelecting] = useState(false);

  /**
   * Toggle photo selection
   */
  const handleToggleSelect = useCallback((photoId: string) => {
    setSelectedIds(prev => {
      const isSelected = prev.includes(photoId);
      const newSelection = isSelected
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId];

      // Enter selection mode on first selection
      if (!isSelecting && newSelection.length > 0) {
        setIsSelecting(true);
      }
      // Exit selection mode when empty
      if (newSelection.length === 0) {
        setIsSelecting(false);
      }

      return newSelection;
    });
  }, [isSelecting]);

  /**
   * Cancel selection
   */
  const handleCancelSelection = useCallback(() => {
    setSelectedIds([]);
    setIsSelecting(false);
  }, []);

  /**
   * Select all photos
   */
  const handleSelectAll = useCallback(() => {
    setSelectedIds(photos.map(p => p.id));
  }, [photos]);

  /**
   * Download selected photos
   */
  const handleDownload = useCallback(() => {
    if (onDownloadSelected && selectedIds.length > 0) {
      onDownloadSelected(selectedIds);
      handleCancelSelection();
    }
  }, [onDownloadSelected, selectedIds, handleCancelSelection]);

  /**
   * Render photo item
   */
  const renderItem = useCallback(
    ({ item }: { item: PhotoWithRelations }) => (
      <PhotoCard
        photo={item}
        onPress={onPhotoPress}
        isSelected={selectedIds.includes(item.id)}
        isSelecting={isSelecting}
        onToggleSelect={handleToggleSelect}
        showStats={showStats}
      />
    ),
    [onPhotoPress, selectedIds, isSelecting, handleToggleSelect, showStats]
  );

  /**
   * Render footer
   */
  const renderFooter = () => {
    if (!hasMore) return null;
    return (
      <View className="py-4 items-center">
        <ActivityIndicator size="small" color={colors.primary} />
      </View>
    );
  };

  /**
   * Render empty state
   */
  const renderEmpty = () => {
    if (isLoading) return null;
    return (
      <View className="flex-1 items-center justify-center py-20">
        <Text style={{ color: colors.textSecondary }}>{emptyMessage}</Text>
      </View>
    );
  };

  return (
    <View className="flex-1">
      <FlatList
        data={photos}
        renderItem={renderItem}
        keyExtractor={(item) => item.id}
        numColumns={3}
        contentContainerStyle={{
          paddingHorizontal: 2,
          paddingTop: 4,
          paddingBottom: isSelecting ? 80 : 20,
        }}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={onRefresh}
            tintColor={colors.primary}
            colors={[colors.primary]}
          />
        }
        onEndReached={onLoadMore}
        onEndReachedThreshold={0.5}
        ListFooterComponent={renderFooter}
        ListEmptyComponent={renderEmpty}
        showsVerticalScrollIndicator={false}
      />

      {/* Selection bar */}
      {isSelecting && (
        <PhotoSelectionBar
          selectedCount={selectedIds.length}
          totalCount={photos.length}
          onCancel={handleCancelSelection}
          onSelectAll={handleSelectAll}
          onDownload={handleDownload}
        />
      )}
    </View>
  );
};

export default PhotoGrid;

